// Logistics → Routes mode panel orchestrator.
// Primary action: + New route.
//
// One route = one unit. The route walks a state machine
// (planned → scheduled → in_transit → delivered, plus cancelled as an
// exit). Carrier is optional at create-time so you can line up the unit
// + stops before locking a hauler in ("carrier-tentative"). The route
// detail card (D3d-part-2) renders the stops timeline + add-stop form
// when a route is selected from the list.
//
// Logistics is an embedded layer per the Admin-split + entitlements
// reframe (docs/design/admin_split_and_entitlements.md §4 + §6.3):
// server-side, logistics endpoints call `assertAnyAnchorEntitlementActive`
// so any active anchor (turbo_sales OR turbo_manufacturing, plus the legacy
// manufacturing_suite wildcard) unlocks the layer. UI-side this panel uses
// `useLogisticsAnchorLayerGate` (anchors endpoint) so turbo_sales-only pilots
// do not hit the Manufacturing Suite entitlement check. Backend tables live under
// the logi_* prefix (logi_routes / logi_stops / logi_carriers) and the
// API surface moved to /api/logi/* alongside that rename.
import { useCallback, useState } from "react";
import { LogisticsGateBanner } from "./LogisticsGateBanner";
import { useLogisticsAnchorLayerGate } from "./useLogisticsAnchorLayerGate";
import { LogisticsRoutesCreateForm } from "./LogisticsRoutesCreateForm";
import { LogisticsShipmentsStrip } from "./LogisticsShipmentsStrip";
import { LogisticsRoutesDetailCard } from "./LogisticsRoutesDetailCard";
import { LogisticsRoutesDispatchBoard } from "./LogisticsRoutesDispatchBoard";
import { LogisticsRoutesList } from "./LogisticsRoutesList";
import { LogisticsRoutesQueueFilters } from "./LogisticsRoutesQueueFilters";
import { LogisticsSerialStatusPrompt } from "./LogisticsSerialStatusPrompt";
import { ghostBtnClass, primaryBtnClass } from "./logisticsRoutesShared";
import { useLogisticsRoutesDispatch } from "./useLogisticsRoutesDispatch";
import { useLogisticsRoutesPanel } from "./useLogisticsRoutesPanel";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export function LogisticsRoutesPanel() {
  const gate = useLogisticsAnchorLayerGate();
  const [navTick, setNavTick] = useState(0);
  const [newSignal, setNewSignal] = useState(0);
  const m = useLogisticsRoutesPanel(navTick, newSignal);
  const handleAfterAssign = useCallback(() => {
    setNavTick((s) => s + 1);
    setDetailNavTick((s) => s + 1);
  }, []);
  const dispatch = useLogisticsRoutesDispatch(
    m.routes,
    m.carriers,
    m.units,
    navTick,
    handleAfterAssign
  );

  // Detail-card selection state. Selecting a row mounts the detail
  // card; the card's own hook reacts to the routeId change. Refresh
  // bumps both navTick (list) and detailNavTick (card) so they stay in
  // sync after add-stop or future state-transition slices.
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [detailNavTick, setDetailNavTick] = useState(0);
  const handleSelectRoute = useCallback((routeId: string) => {
    setSelectedRouteId((prev) => (prev === routeId ? null : routeId));
  }, []);
  const handleCloseDetail = useCallback(() => setSelectedRouteId(null), []);
  // After a successful state transition the detail hook reloads itself.
  // Bump the LIST nav-tick too so the row's state chip + (eventual)
  // terminal-state filtering reflect the new state without forcing the
  // user to hit Refresh.
  const handleAfterStateChange = useCallback(() => {
    setNavTick((s) => s + 1);
  }, []);

  return (
    <div
      {...(gate.enabled ? { [FIREFLY_TARGET_ATTR]: "logi-routes-surface" } : {})}
      className="w-full text-left space-y-4"
    >
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Routes</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={primaryBtnClass}
            onClick={() => setNewSignal((s) => s + 1)}
            disabled={!gate.enabled || gate.loading}
          >
            + New route
          </button>
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => {
              gate.refresh();
              setNavTick((s) => s + 1);
              setDetailNavTick((s) => s + 1);
            }}
            disabled={gate.loading || m.listBusy || m.actionBusy}
          >
            {m.listBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <LogisticsGateBanner gate={gate} />
      {gate.enabled ? <LogisticsSerialStatusPrompt /> : null}
      {gate.enabled && m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}

      {gate.enabled ? (
        <>
          <LogisticsShipmentsStrip
            units={m.units}
            enabled={gate.enabled}
            navTick={navTick}
            onChanged={() => setNavTick((s) => s + 1)}
          />
          <LogisticsRoutesCreateForm m={m} />
          <LogisticsRoutesQueueFilters m={m} />
          <LogisticsRoutesList
            m={m}
            selectedRouteId={selectedRouteId}
            onSelectRoute={handleSelectRoute}
          />
          <LogisticsRoutesDispatchBoard d={dispatch} />
          {selectedRouteId ? (
            <LogisticsRoutesDetailCard
              listModel={m}
              routeId={selectedRouteId}
              detailNavTick={detailNavTick}
              onAfterStateChange={handleAfterStateChange}
              onClose={handleCloseDetail}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
